import { LoaderFileKeys } from "../constant/AppConstants";
import { LabelButton } from "../../ui/LabelButton";
import { TablePopupWindow } from "./TablePopupWindow";
import { AppSettings } from "../managers/AppSettings";
import { RectSize } from "../../utils/RectSize";
import { IInteractive } from "../../interfaces/IInteractive";
import { ButtonBase, ButtonEvents } from "../../ui/ButtonBase";
import { AppLocalization } from "../localization/AppLocalization";
import { Padding } from "../../utils/Padding";
import { AppAudioPlayer, SoundEffectKeys } from "../../audio/AppAudioPlayer";
import { ScreenManager } from "../../managers/ScreenManager";


export enum PopupBinaryChoiceType {
    PRIMARY,
    SECONDARY,
}

export class PopupBinary extends TablePopupWindow
{
    public nsContentBg: Phaser.GameObjects.NineSlice;
    public nsContentBgExtrusion: number = 25; // extrude to cover for outer drop shadow size
    public txtTitle: Phaser.GameObjects.Text;
    public btnPrimary: LabelButton;
    public btnSecondary: LabelButton;
    // Auto-enable on Show and auto-disable on Hide all interactives eg buttons
    protected interactives: IInteractive[];
    protected padding: Padding;
    protected spacing: Padding;
    protected titlePadding: Padding;
    protected useOverlay: boolean;


    public override Init(useOverlay: boolean = false): void
    {
        // >> create screenOverlayHitArea
        super.Init();

        this.useOverlay = useOverlay;

        this.interactives = [];
        this.padding = new Padding(80, 60);
        this.spacing = new Padding(30);
        this.titlePadding = new Padding(0, 0, 0, 20);
    }

    public override Build(): void
    {
        // console.log("PopupBinary::Build");

        // overlay
        super.Build();

        const btnScale: number = 0.8;

        const nsContentBgBorderSize = 55;
        this.nsContentBg = this.scene.make.nineslice({
            key: LoaderFileKeys.ui,
            frame: "rect-black-w72-r24-A80-ds.png",
            leftWidth: nsContentBgBorderSize, 
            rightWidth: nsContentBgBorderSize, 
            topHeight: nsContentBgBorderSize, 
            bottomHeight: nsContentBgBorderSize,
            // alpha: 0.9, // text already has an alpha?
        });
        this.add(this.nsContentBg);

        // txtTitle
        this.txtTitle = this.scene.make.text({
            style: {
                color: AppSettings.colors.whiteString,
                fontSize: "50px",
                fontFamily: AppSettings.fontFamilies.MontserratBold,
                align: "center",
                metrics: AppSettings.metrics.montserratBold50,
            },
        });
        this.txtTitle.setOrigin(0.5, 0);
        this.add(this.txtTitle);

        // btnPrimary
        this.btnPrimary = new LabelButton(this.scene);
        this.btnPrimary.setData({choiceType:PopupBinaryChoiceType.PRIMARY});
        this.btnPrimary.InitBase({
            out:{texture:LoaderFileKeys.ui, frame:"btn-oval3-bg-out.png"},
        });
        this.btnPrimary.InitLabel({
                out: {
                    color: "#000",
                    fontSize: "55px",
                    fontFamily: AppSettings.fontFamilies.MontserratBold,
                    metrics: AppSettings.metrics.montserratBold60,
                },
                disabled: {
                    color: "#999",
                    fontSize: "55px",
                    fontFamily: AppSettings.fontFamilies.MontserratBold,
                    metrics: AppSettings.metrics.montserratBold60,
                }
            }
        );
        this.btnPrimary.label.setOrigin(0.5);
        this.btnPrimary.PostInit(20); // sets interactive
        this.btnPrimary.setScale(btnScale);
        this.interactives.push(this.btnPrimary);
        this.add(this.btnPrimary);

        // btnSecondary
        this.btnSecondary = new LabelButton(this.scene);
        this.btnSecondary.setData({choiceType:PopupBinaryChoiceType.SECONDARY});
        this.btnSecondary.InitBase({
            out:{texture:LoaderFileKeys.ui, frame:"btn-oval3-bg-out.png"},
        });
        this.btnSecondary.InitLabel(
            {
                out: {
                    color: "#000",
                    fontSize: "55px",
                    fontFamily: AppSettings.fontFamilies.MontserratBold,
                    metrics: AppSettings.metrics.montserratBold60,
                },
                disabled: {
                    color: "#999",
                    fontSize: "55px",
                    fontFamily: AppSettings.fontFamilies.MontserratBold,
                    metrics: AppSettings.metrics.montserratBold60,
                }
            }
        );
        this.btnSecondary.label.setOrigin(0.5);
        this.btnSecondary.PostInit(20);
        this.btnSecondary.setScale(btnScale);
        this.interactives.push(this.btnSecondary);
        this.add(this.btnSecondary);
    }

    public SetTitle(sTitle: string, updateView: boolean = false): void
    {
        this.txtTitle.setText(sTitle);

        if (updateView) {
            this.UpdateView();
        }
    }

    public SetPrimaryButtonLabel(sTitle: string, updateView: boolean = false): void
    {
        this.btnPrimary.SetText(sTitle);

        if (updateView) {
            this.UpdateView();
        }
    }

    public SetSecondaryButtonLabel(sTitle: string, updateView: boolean = false): void
    {
        this.btnSecondary.SetText(sTitle);

        if (updateView) {
            this.UpdateView();
        }
    }

    /**
     * update children transforms (pos, scale..)
     */
    public UpdateView(): void
    {
        // purge previous wrodWrap setting before measuring total content width
        // this.txtTitle.style.wordWrapWidth = null;

        // content size (excluding drop shadow size [all sides] - the black part of nsContentBg rect)
        const contentWidth = 2 * this.padding.horizontal + Math.max(
            this.btnPrimary.view.displayWidth * this.btnPrimary.scale + this.spacing.horizontal,
            this.txtTitle.displayWidth,
        );

        // 0.5, 0
        // this.txtTitle.style.wordWrapWidth = contentWidth;

        const contentHeight = 2 * this.padding.vertical + this.titlePadding.top + this.txtTitle.displayHeight + this.titlePadding.bottom + 2 * this.spacing.vertical + 2 * this.btnPrimary.view.displayHeight * this.btnPrimary.scale;

        this.nsContentBg.setSize(
            contentWidth + 2 * this.nsContentBgExtrusion, 
            contentHeight + 2 * this.nsContentBgExtrusion,
        );

        // position
        const btnHeight = this.btnPrimary.view.displayHeight * this.btnPrimary.scale;
        let positionY = - contentHeight * 0.5 + this.padding.vertical + this.titlePadding.top;
        this.txtTitle.y = positionY;
        const btnHeightHalf = btnHeight * 0.5;
        positionY += (this.txtTitle.displayHeight + this.titlePadding.bottom + this.spacing.vertical + btnHeightHalf);
        // btns : 165/280
        this.btnPrimary.y = positionY;
        positionY += (this.spacing.vertical + btnHeight);
        this.btnSecondary.y = positionY;
    }

    protected RegisterListeners(): void
    {
        for (let i = 0; i < this.interactives.length; i++) {
            const btn = this.interactives[i] as ButtonBase;
            btn.on(ButtonEvents.CLICK, this.OnButtonClicked, {root:this, target:btn});
        }
        this.imgScreenOverlay.on(Phaser.Input.Events.GAMEOBJECT_POINTER_UP, this.OnOverlayClick, this);
    }

    protected UnRegisterListeners(): void
    {
        for (let i = 0; i < this.interactives.length; i++) {
            const btn = this.interactives[i] as ButtonBase;
            btn.off(ButtonEvents.CLICK, this.OnButtonClicked);
        }
        this.imgScreenOverlay.off(Phaser.Input.Events.GAMEOBJECT_POINTER_UP, this.OnOverlayClick);
    }
    
    public override Show(animated: boolean = true): void
    {
        super.Show(animated);
        this.RegisterListeners();
        this.SetInteractive(true);
        this.OnResizeManual();
    }

    public override Hide(animated: boolean = true): void
    {
        super.Hide(animated);
        this.UnRegisterListeners();
        this.SetInteractive(false);
    }
    
    public SetInteractive(value: boolean): void
    {
        for (let i = 0; i < this.interactives.length; i++) {
            const btn = this.interactives[i];
            btn.SetInteractive(value);
        }
    }

    public override SetData(inputData:any): void
    {
        super.SetData(inputData);
    }

    public OnButtonClicked(): void
    {
        const root: PopupBinary = this["root"] as PopupBinary;
        const btn: ButtonBase = this["target"] as ButtonBase;
        const choiceType: PopupBinaryChoiceType = btn.getData("choiceType");

        // button click
        AppAudioPlayer.PlaySfx(SoundEffectKeys.click5);
        
        root.Hide();
        root.inputData.onComplete(root.inputData.taskType, {choiceType:choiceType});
    }

    protected OnOverlayClick(): void {
        this.Hide();
        this.inputData.onComplete(this.inputData.taskType, {choiceType:PopupBinaryChoiceType.SECONDARY});
    }
    
    protected OnResizeManual(): void {
        const windowRect: RectSize = ScreenManager.GetWindowRect();
        this.OnResize(windowRect);
    }

    /**
     * cancel base functionality
     * - PopupWindow::screenOverlay will NOT be drawn
     */
    public override OnResize(windowRect: RectSize): void {
        if (this.useOverlay) {
            const scaledWindowWidth = windowRect.width / this.scaleX;
            const scaledWindowHeight = windowRect.height / this.scaleY;
            this.imgScreenOverlay.setDisplaySize(
                scaledWindowWidth, 
                scaledWindowHeight
            );
            this.imgScreenOverlay.setPosition(
                -this.imgScreenOverlay.displayWidth * 0.5, 
                -this.imgScreenOverlay.displayHeight * 0.5
            );
            this.imgScreenOverlay.setInteractive();
        }
    }
}